import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, User as UserIcon, Video, CheckCircle2, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

interface ClassRow {
  id: string;
  grade: number;
  subject: string | null;
  title: string;
  description: string | null;
  teacherName: string;
  scheduledAt: string;
  durationMinutes: number;
  meetingUrl: string | null;
  status: "upcoming" | "live" | "ended" | "cancelled";
  recordingUrl: string | null;
  isRegistered: boolean;
}

function formatLocal(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", { timeZone: "America/Jamaica", weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
}

function isJoinable(cls: ClassRow): boolean {
  if (cls.status !== "upcoming" && cls.status !== "live") return false;
  if (!cls.meetingUrl) return false;
  const start = new Date(cls.scheduledAt).getTime();
  const end = start + cls.durationMinutes * 60 * 1000;
  const now = Date.now();
  // Joinable from 10 min before start until end of class
  return now >= start - 10 * 60 * 1000 && now <= end;
}

export default function Classes() {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const { data: classes = [], isLoading, error } = useQuery<ClassRow[]>({
    queryKey: ["/api/live-classes"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/live-classes");
      return res.json();
    },
    retry: false,
  });

  const register = useMutation({
    mutationFn: async (id: string) => (await apiRequest("POST", `/api/live-classes/${id}/register`, {})).json(),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-classes"] });
      toast({ title: "You're registered", description: "We'll send a reminder the day before." });
    },
    onError: (e: any) => toast({ title: "Registration failed", description: e?.message, variant: "destructive" }),
  });

  const unregister = useMutation({
    mutationFn: async (id: string) => (await apiRequest("DELETE", `/api/live-classes/${id}/register`)).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/live-classes"] });
      toast({ title: "Unregistered" });
    },
  });

  const join = useMutation({
    mutationFn: async (id: string) => (await apiRequest("POST", `/api/live-classes/${id}/join`, {})).json(),
    onSuccess: (data: { meetingUrl?: string }) => {
      if (data.meetingUrl) window.open(data.meetingUrl, "_blank");
    },
    onError: (e: any) => toast({ title: "Could not open class", description: e?.message, variant: "destructive" }),
  });

  // 404 = feature off, 403 = no active sub
  const featureOff = (error as any)?.status === 404 || (error as any)?.message?.includes("Not found");

  if (isLoading) return <div className="container mx-auto p-6">Loading classes…</div>;

  if (featureOff) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card>
          <CardHeader><CardTitle>Live Classes</CardTitle><CardDescription>Live classes aren't available yet. Check back soon.</CardDescription></CardHeader>
        </Card>
      </div>
    );
  }

  const upcoming = classes.filter((c) => c.status !== "ended" && c.status !== "cancelled");
  const past = classes.filter((c) => c.status === "ended");

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-3xl">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>

      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">Live Classes</h1>
        <p className="text-muted-foreground mt-1">Classes for your grade. Register to get reminders and a post-class quiz.</p>
      </div>

      {classes.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-muted-foreground">No classes scheduled yet. Check back soon.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {upcoming.length > 0 && (
            <section className="mb-8">
              <h2 className="text-lg font-semibold mb-3">Upcoming</h2>
              <div className="space-y-3">
                {upcoming.map((cls) => (
                  <Card key={cls.id} className={cls.isRegistered ? "border-green-500/40" : ""}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            {cls.subject && <Badge variant="outline">{cls.subject}</Badge>}
                            {cls.isRegistered && <Badge className="bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/30">Registered</Badge>}
                          </div>
                          <h3 className="font-semibold text-base">{cls.title}</h3>
                          {cls.description && <p className="text-sm text-muted-foreground mt-1">{cls.description}</p>}
                          <div className="flex flex-col gap-1 mt-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-2"><UserIcon className="h-3.5 w-3.5" /> {cls.teacherName}</span>
                            <span className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5" /> {formatLocal(cls.scheduledAt)}</span>
                            <span className="flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> {cls.durationMinutes} minutes</span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          {isJoinable(cls) && cls.isRegistered ? (
                            <Button onClick={() => join.mutate(cls.id)} className="bg-blue-500 hover:bg-blue-600">
                              <Video className="h-4 w-4 mr-2" /> Join Class
                            </Button>
                          ) : cls.isRegistered ? (
                            <Button variant="outline" onClick={() => unregister.mutate(cls.id)} disabled={unregister.isPending}>
                              Unregister
                            </Button>
                          ) : (
                            <Button onClick={() => register.mutate(cls.id)} disabled={register.isPending}>
                              Register
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {past.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold mb-3">Recent</h2>
              <div className="space-y-3">
                {past.slice(0, 10).map((cls) => (
                  <Card key={cls.id} className="opacity-90">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <Badge variant="secondary">Ended</Badge>
                            {cls.subject && <Badge variant="outline">{cls.subject}</Badge>}
                          </div>
                          <h3 className="font-semibold">{cls.title}</h3>
                          <p className="text-sm text-muted-foreground">{cls.teacherName} · {formatLocal(cls.scheduledAt)}</p>
                        </div>
                        <div className="flex flex-col gap-2">
                          {cls.isRegistered && (
                            <Button size="sm" variant="outline" onClick={() => navigate(`/classes/${cls.id}/quiz`)}>
                              <CheckCircle2 className="h-4 w-4 mr-1" /> Post-class Quiz
                            </Button>
                          )}
                          {cls.recordingUrl && (
                            <Button size="sm" variant="outline" onClick={() => window.open(cls.recordingUrl!, "_blank")}>
                              <Video className="h-4 w-4 mr-1" /> Recording
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
